import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { shouldUseComplexAnimations } from '../utils/performance';
import {
  FolderOpenIcon,
  AdjustmentsHorizontalIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  EyeIcon,
  CogIcon,
  ShieldCheckIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: FolderOpenIcon,
    title: 'Gestion de dossiers multiples',
    description: 'Scannez et gérez plusieurs dossiers source simultanément pour une organisation optimale de votre bibliothèque de fichiers source.',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    icon: AdjustmentsHorizontalIcon,
    title: 'Filtres avancés',
    description: 'Filtrez la copie de vos MP3 par année de sortie, durée de piste, et nombre de fichiers avec des contrôles précis et intuitifs.',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: DocumentDuplicateIcon,
    title: 'Copie intelligente',
    description: 'Copiez automatiquement les fichiers sélectionnés vers votre dossier de destination avec gestion des doublons.',
    color: 'from-green-500 to-emerald-500'
  },
  {
    icon: ChartBarIcon,
    title: 'Statistiques détaillées',
    description: 'Visualisez des statistiques complètes sur votre collection : nombre de fichiers en cache, nombre de dossiers présents et plus !',
    color: 'from-orange-500 to-red-500'
  },
  {
    icon: EyeIcon,
    title: 'Aperçu avant action',
    description: 'Prévisualisez les fichiers qui seront copiés avant de confirmer l\'opération pour éviter les erreurs.',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    icon: CogIcon,
    title: 'Paramètres personnalisables',
    description: 'Ajustez les limites des filtres, gérez le cache, et personnalisez l\'interface selon vos préférences.',
    color: 'from-teal-500 to-blue-500'
  },
  {
    icon: ShieldCheckIcon,
    title: 'Sécurité et fiabilité',
    description: 'Opérations sécurisées avec vérification des erreurs et protection contre la perte de données.',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    icon: BoltIcon,
    title: 'Performance optimisée',
    description: 'Scan rapide avec mise en cache intelligente pour une expérience utilisateur fluide et réactive.',
    color: 'from-pink-500 to-rose-500'
  }
];

export default function Features() {
  const [enableHoverEffects, setEnableHoverEffects] = useState(true);

  useEffect(() => {
    // Détecte si les animations complexes doivent être désactivées
    setEnableHoverEffects(shouldUseComplexAnimations());
  }, []);

  return (
    <>
      <section id="features" className="py-20 bg-gray-900/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-primary-300">Tout ce dont vous avez besoin</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              De la sélection aléatoire à la copie automatique — chaque fonctionnalité est pensée pour vous faire gagner du temps sur la gestion de votre bibliothèque MP3
            </p>
          </motion.div>

          {/* Feature différenciante en avant */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="max-w-2xl mx-auto card p-10 border-2 border-accent-500/50">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl flex items-center justify-center mb-6 mx-auto">
                <EyeIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 text-center">
                {features[4].title}
              </h3>
              <p className="text-gray-300 text-center leading-relaxed">
                {features[4].description}
              </p>
            </div>
          </motion.div>

          {/* Autres features en grille réduite */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.filter((_, i) => i !== 4).map((feature, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
                viewport={{ once: true }}
                whileHover={enableHoverEffects ? { scale: 1.02 } : undefined}
                className="card group"
              >
                <div
                  className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <feature.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-lg font-semibold text-white mb-3">
                  {feature.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vidéo de présentation */}
      <section className="my-16 flex flex-col items-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">
          <span className="text-primary-300">Présentation en vidéo</span>
        </h2>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-12">
          Découvrez le logiciel de copie MP3 en action live !
        </p>
        <video
          src="/videos/video_presentation_logiciel.mp4"
          width={920}
          height={720}
          controls
          preload="metadata"
          style={{ maxWidth: '100%', height: 'auto', borderRadius: '1rem', boxShadow: '0 4px 24px #0002' }}
        >
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      </section>
    </>
  );
}
